import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { tenantApi } from '../../api/tenantApi';
import { Panel, Loader, EmptyState, useToast, errText, useCaptchaConfirm, TenantSelect } from '../ui';

// ---------------------------------------------------------------------------
// Flags — `key` is what we keep in component state, `api` is the field name
// used by /api/CPanel/GetPermission + SavePermissionBulk.
// ---------------------------------------------------------------------------
const FLAGS = [
  { key: 'read', api: 'uRead', label: 'Read' },
  { key: 'write', api: 'uWrite', label: 'Write' },
  { key: 'modify', api: 'uModify', label: 'Modify' },
  { key: 'delete', api: 'uDelete', label: 'Delete' },
  { key: 'print', api: 'uPrint', label: 'Print' },
  { key: 'approval', api: 'uApproval', label: 'Approval' },
];
const ALL_KEYS = FLAGS.map((f) => f.key);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// The endpoints return a bare array; be tolerant if they ever wrap it.
const toList = (res) =>
  Array.isArray(res) ? res
    : Array.isArray(res?.data) ? res.data
    : Array.isArray(res?.result) ? res.result
    : [];

const truthy = (v) => v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';

const pick = (obj, keys) => {
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// Accepts uRead / URead / read / Read so a small casing difference on the
// backend won't break the page.
function normalizeRow(r) {
  const row = {
    moduleId: pick(r, ['moduleId', 'ModuleId']),
    moduleName: pick(r, ['moduleName', 'ModuleName']) ?? '',
    formId: pick(r, ['formId', 'FormId']),
    formName: pick(r, ['formName', 'FormName']) ?? '',
  };
  FLAGS.forEach((f) => {
    row[f.key] = truthy(pick(r, [f.api, cap(f.api), f.key, cap(f.key)]));
  });
  return row;
}

const rowKey = (r) => `${r.moduleId}:${r.formId}`;
const sig = (r) => FLAGS.map((f) => (r[f.key] ? 1 : 0)).join('');

// checked = every flag on, indeterminate = some on
function summarize(list, keys) {
  let on = 0;
  let total = 0;
  list.forEach((r) => keys.forEach((k) => { total += 1; if (r[k]) on += 1; }));
  return { checked: total > 0 && on === total, indeterminate: on > 0 && on < total };
}

function TriCheck({ checked, indeterminate, onChange, disabled, title }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={!!checked}
      disabled={disabled}
      title={title}
      onChange={(e) => onChange(e.target.checked)}
    />
  );
}

// ---------------------------------------------------------------------------
// SearchSelect — dropdown with a search box. The list is position:fixed so a
// Panel with overflow:hidden can't clip it.
//   options = [{ value, label }]      onChange receives the option's value
// ---------------------------------------------------------------------------
function SearchSelect({ id, options, value, onChange, placeholder = 'select…', disabled, width = 200 }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 260 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const listRef = useRef(null);

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const w = Math.max(r.width, 240);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
    setPos({ top: r.bottom + 4, left, width: w });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, place]);

  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

  // keep the highlighted option visible when navigating with the arrow keys
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector('.ss__opt.hi')
      ?.scrollIntoView({ block: 'nearest' });
  }, [hi, open]);

  const needle = q.trim().toLowerCase();
  const filtered = needle ? options.filter((o) => String(o.label).toLowerCase().includes(needle)) : options;
  const selected = options.find((o) => String(o.value) === String(value));

  const toggle = () => {
    setOpen((o) => !o);
    setQ('');
    // start the highlight on the currently selected option
    setHi(Math.max(0, options.findIndex((o) => String(o.value) === String(value))));
  };
  const choose = (o) => { onChange(o.value); setOpen(false); };

  const onSearchKey = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      setOpen(false);
      btnRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHi((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[hi]) choose(filtered[hi]);
    }
  };

  return (
    <>
      <button
        id={id}
        ref={btnRef}
        type="button"
        className="ss__btn perm-select"
        style={{ width }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggle}
      >
        <span className="ss__label">{selected ? selected.label : placeholder}</span>
        <span className="ss__caret">{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div
          ref={panelRef}
          className="ss__panel"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
        >
          <div className="ss__search">
            <input
              className="perm-select"
              style={{ width: '100%', boxSizing: 'border-box' }}
              autoFocus
              placeholder="search…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setHi(0); }}
              onKeyDown={onSearchKey}
            />
          </div>
          <div className="ss__list" role="listbox" ref={listRef}>
            {filtered.length === 0 && <div className="ss__empty">no match</div>}
            {filtered.map((o, i) => (
              <button
                key={`${i}-${String(o.value)}`}
                type="button"
                role="option"
                aria-selected={String(o.value) === String(value)}
                className={`ss__opt${i === hi ? ' hi' : ''}${String(o.value) === String(value) ? ' sel' : ''}`}
                onMouseEnter={() => setHi(i)}
                onClick={() => choose(o)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

const CSS = `
.perm-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px 28px;margin-bottom:16px}
.perm-field{display:flex;align-items:center;gap:12px}
.perm-field label{flex:0 0 140px;text-align:right;font-size:12px;font-weight:600;color:var(--text-dim,#8b949e)}
.perm-select{flex:1;min-width:0;padding:8px 10px;font:inherit;font-size:12.5px;color:var(--text,#c9d1d9);background:var(--bg-input,#0d1117);border:1px solid var(--border,#30363d);border-radius:4px}
.perm-select:disabled{opacity:.5}
.perm-modbar{display:flex;gap:20px;overflow-x:auto;padding:10px 12px;margin-bottom:12px;background:var(--panel-alt,#10243b);border:1px solid var(--border,#30363d);border-radius:4px}
.perm-chip{display:flex;align-items:center;gap:6px;white-space:nowrap;font-size:12px;cursor:pointer}
.perm-actions{display:flex;align-items:center;gap:10px;margin-bottom:12px}
.perm-dirty{font-size:11.5px;color:var(--amber,#d29922)}
.perm-tablewrap{max-height:60vh;overflow:auto;border:1px solid var(--border,#30363d);border-radius:4px}
.perm-tablewrap thead th{position:sticky;top:0;z-index:1;background:var(--panel,#0d1117)}
.perm-th{display:inline-flex;align-items:center;gap:6px;white-space:nowrap}
.perm-mod{font-weight:700}
.perm-row--dirty td{background:rgba(210,153,34,.08)}
.perm-overlay{position:fixed;inset:0;z-index:9000;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.6)}
.perm-snack{position:fixed;left:50%;bottom:28px;transform:translateX(-50%);z-index:11000;display:flex;align-items:center;gap:14px;max-width:min(560px,92vw);padding:11px 16px;border-radius:6px;font-size:13px;color:#fff;box-shadow:0 10px 30px rgba(0,0,0,.5);animation:perm-snack-in .2s ease-out}
.perm-snack--success{background:#1f7a3f;border:1px solid var(--green,#3dff7f)}
.perm-snack--error{background:#8b2630;border:1px solid var(--danger,#e5484d)}
.perm-snack button{background:transparent;border:0;color:inherit;font-size:16px;line-height:1;cursor:pointer}
@keyframes perm-snack-in{from{opacity:0;transform:translate(-50%,8px)}to{opacity:1;transform:translate(-50%,0)}}
.perm-spinner{width:56px;height:56px;border:5px solid rgba(255,255,255,.2);border-top-color:var(--green,#3dff7f);border-radius:50%;animation:perm-spin .8s linear infinite}
@keyframes perm-spin{to{transform:rotate(360deg)}}
.ss__btn{display:inline-flex;align-items:center;justify-content:space-between;gap:8px;text-align:left;cursor:pointer}
.ss__btn:disabled{cursor:not-allowed}
.ss__label{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ss__caret{opacity:.7}
.ss__panel{position:fixed;z-index:10000;background:var(--bg-input,#0d1117);border:1px solid var(--green,#3dff7f);border-radius:4px;box-shadow:0 12px 32px rgba(0,0,0,.6)}
.ss__search{padding:8px}
.ss__list{max-height:240px;overflow:auto}
.ss__opt{display:block;width:100%;padding:7px 12px;font:inherit;font-size:12.5px;text-align:left;color:var(--text,#c9d1d9);background:transparent;border:0;cursor:pointer}
.ss__opt.hi{background:rgba(61,255,127,.10)}
.ss__opt.sel{color:var(--green,#3dff7f);font-weight:600}
.ss__empty{padding:12px;font-size:12px;opacity:.7}
@media (max-width:900px){
  .perm-grid{grid-template-columns:1fr}
  .perm-field label{flex-basis:110px}
}
`;

// Reachable two ways:
//  - /admin/tenants/:clientCode/permissions   (linked from Tenant Detail)
//  - /admin/tenants/tools/permissions          (linked from the sidebar directly)
export default function Permissions() {
  const { clientCode: routeClientCode } = useParams();
  const toast = useToast();
  const [confirm, confirmNode] = useCaptchaConfirm();

  const [clientCode, setClientCode] = useState(routeClientCode || '');

  // dropdown data
  const [orgs, setOrgs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [groups, setGroups] = useState([]);
  // Module dropdown options. Captured from an UNFILTERED load ('all') so the
  // list doesn't shrink to one module once a module filter is applied.
  const [moduleOptions, setModuleOptions] = useState([]);

  // dropdown selection (kept as strings for <select>)
  const [orgId, setOrgId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [moduleFilter, setModuleFilter] = useState('all');

  // permission grid
  const [rows, setRows] = useState([]);
  const [origRows, setOrigRows] = useState([]);

  const [lookupLoading, setLookupLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Page-local snackbar for the save result (independent of the shared toast).
  const [snack, setSnack] = useState(null); // { type: 'success' | 'error', msg, id }
  const showSnack = (type, msg) => setSnack({ type, msg, id: Date.now() });
  useEffect(() => {
    if (!snack) return undefined;
    const t = setTimeout(() => setSnack(null), 5000);
    return () => clearTimeout(t);
  }, [snack]);

  useEffect(() => {
    if (routeClientCode) setClientCode(routeClientCode);
  }, [routeClientCode]);

  // ---- 1. Tenant chosen -> organizations + user roles ---------------------
  useEffect(() => {
    setOrgs([]); setOrgId('');
    setBranches([]); setBranchId('');
    setGroups([]); setGroupId('');
    setModuleFilter('all'); setModuleOptions([]);
    setRows([]); setOrigRows([]);
    if (!clientCode) return undefined;

    let cancelled = false;
    setLookupLoading(true);
    (async () => {
      try {
        const [orgRes, grpRes] = await Promise.all([
          tenantApi.getCpanelOrganizations(clientCode),
          tenantApi.getCpanelUserGroups(clientCode),
        ]);
        if (cancelled) return;
        const o = toList(orgRes);
        const g = toList(grpRes);
        setOrgs(o);
        setGroups(g);
        if (o.length) setOrgId(String(o[0].schoolId));
        if (g.length) setGroupId(String(g[0].userGroupId));
      } catch (err) {
        if (!cancelled) toast.error(errText(err));
      } finally {
        if (!cancelled) setLookupLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientCode]); // eslint-disable-line

  // ---- 2. Organization chosen -> branches ---------------------------------
  useEffect(() => {
    setBranches([]); setBranchId('');
    setModuleFilter('all'); setModuleOptions([]); // modules belong to org/branch
    if (!clientCode || orgId === '') return undefined;

    let cancelled = false;
    (async () => {
      try {
        const res = await tenantApi.getCpanelBranches(orgId, clientCode);
        if (cancelled) return;
        // GetBranch returns a base64 logo per branch — drop it, we don't need it here.
        const list = toList(res).map(({ bytLogo, ...b }) => b); // eslint-disable-line no-unused-vars
        setBranches(list);
        if (list.length) setBranchId(String(list[0].branchId));
      } catch (err) {
        if (!cancelled) toast.error(errText(err));
      }
    })();
    return () => { cancelled = true; };
  }, [clientCode, orgId]); // eslint-disable-line

  // Changing branch also invalidates the module list.
  useEffect(() => {
    setModuleFilter('all'); setModuleOptions([]);
  }, [branchId]);

  // ---- 3. Org + branch + role (+ module) chosen -> permission rows --------
  // Hits GET /api/ModuleForm/GetPermissions. moduleId is sent only when a
  // module is selected; formId is never sent (always null).
  const loadSeq = useRef(0);
  const loadPermissions = useCallback(async () => {
    const seq = ++loadSeq.current;
    if (!clientCode || orgId === '' || branchId === '' || groupId === '') {
      setRows([]); setOrigRows([]); setLoading(false);
      return;
    }
    setLoading(true);
    try {
      if (typeof tenantApi.getModulePermissions !== 'function') {
        throw new Error('tenantApi.getModulePermissions is not defined — add it to tenantApi.js');
      }
      const res = await tenantApi.getModulePermissions({
        clientCode,
        organisationId: Number(orgId),
        branchId: Number(branchId),
        userGroupId: Number(groupId),
        moduleId: moduleFilter === 'all' ? null : Number(moduleFilter),
      });
      if (seq !== loadSeq.current) return; // a newer request superseded this one
      const list = toList(res).map(normalizeRow).filter((r) => r.formId != null);
      setRows(list);
      setOrigRows(list.map((r) => ({ ...r })));

      if (moduleFilter === 'all') {
        const seen = new Map();
        list.forEach((r) => {
          if (!seen.has(r.moduleId)) seen.set(r.moduleId, { id: r.moduleId, name: r.moduleName });
        });
        setModuleOptions([...seen.values()]);
      }
    } catch (err) {
      if (seq !== loadSeq.current) return;
      toast.error(errText(err));
      setRows([]); setOrigRows([]);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [clientCode, orgId, branchId, groupId, moduleFilter]); // eslint-disable-line

  useEffect(() => { loadPermissions(); }, [loadPermissions]);

  // ---- derived ------------------------------------------------------------
  const activeFilter = moduleFilter;
  const inView = useCallback(
    (r) => activeFilter === 'all' || String(r.moduleId) === activeFilter,
    [activeFilter]
  );
  const visibleRows = useMemo(() => rows.filter(inView), [rows, inView]);
  const visibleModules = useMemo(
    () => (activeFilter === 'all' ? moduleOptions : moduleOptions.filter((m) => String(m.id) === activeFilter)),
    [moduleOptions, activeFilter]
  );

  const origSigs = useMemo(() => new Map(origRows.map((r) => [rowKey(r), sig(r)])), [origRows]);
  const dirtyRows = useMemo(() => rows.filter((r) => origSigs.get(rowKey(r)) !== sig(r)), [rows, origSigs]);
  const dirtyKeys = useMemo(() => new Set(dirtyRows.map(rowKey)), [dirtyRows]);
  const dirtyCount = dirtyRows.length;

  const busy = saving || loading;
  const selectionReady = clientCode && orgId !== '' && branchId !== '' && groupId !== '';

  // ---- selection handlers (guarded against losing unsaved edits) ----------
  const guard = (fn) => (e) => {
    const value = e.target.value;
    if (dirtyCount > 0 && !window.confirm('You have unsaved changes. Discard them?')) return;
    fn(value);
  };

  // Module change refetches from the API, so it needs the same guard.
  const changeModule = (v) => {
    if (dirtyCount > 0 && !window.confirm('You have unsaved changes. Discard them?')) return;
    setModuleFilter(String(v));
  };

  // ---- checkbox mutators --------------------------------------------------
  const applyFlags = (match, keys, value) =>
    setRows((rs) =>
      rs.map((r) => {
        if (!match(r)) return r;
        const next = { ...r };
        keys.forEach((k) => { next[k] = value; });
        return next;
      })
    );

  const toggleCell = (row, flag, value) => applyFlags((r) => rowKey(r) === rowKey(row), [flag], value);
  const toggleRow = (row, value) => applyFlags((r) => rowKey(r) === rowKey(row), ALL_KEYS, value);
  const toggleColumn = (flag, value) => applyFlags(inView, [flag], value);
  const toggleAll = (value) => applyFlags(inView, ALL_KEYS, value);
  const toggleModule = (moduleId, value) => applyFlags((r) => r.moduleId === moduleId, ALL_KEYS, value);

  // ---- save / cancel ------------------------------------------------------
  const handleCancel = () => setRows(origRows.map((r) => ({ ...r })));

  const handleSave = async () => {
    if (!dirtyCount) return;

    const moduleIds = [...new Set(dirtyRows.map((r) => r.moduleId))];
    const groupName = groups.find((g) => String(g.userGroupId) === groupId)?.userGroupName || groupId;
    const ok = await confirm(
      `Save permission changes for role "${groupName}"? ${dirtyCount} form(s) in ${moduleIds.length} module(s) will be updated.`
    );
    if (!ok) return;

    setSaving(true); // full-screen loader on
    const done = [];
    const serverMsgs = [];
    let failed = false;
    try {
      // SavePermissionBulk works one module at a time. For every module that
      // has a change we send ALL of that module's forms, so the result is the
      // same whether the backend merges or replaces.
      for (const moduleId of moduleIds) {
        const permissions = rows
          .filter((r) => r.moduleId === moduleId)
          .map((r) => {
            const p = { formId: Number(r.formId) };
            FLAGS.forEach((f) => { p[f.api] = !!r[f.key]; });
            return p;
          });
        const res = await tenantApi.savePermissionBulk(
          { orgId, branchId, clientCode },
          { userGroupId: Number(groupId), moduleId: Number(moduleId), permissions }
        );
        // e.g. { msg: "9 of 9 permissions saved successfully!", status: "Success", userRoleIds: [...] }
        if (res?.status && String(res.status).toLowerCase() !== 'success') {
          throw new Error(res.msg || 'Save failed');
        }
        if (res?.msg) serverMsgs.push(res.msg);
        done.push(moduleId);
      }
      // Save API finished: loader off, then show the API's own message.
      setSaving(false);
      showSnack('success', serverMsgs.length ? serverMsgs.join(' ') : `Permissions saved for role "${groupName}".`);
    } catch (err) {
      failed = true;
      setSaving(false);
      showSnack(
        'error',
        done.length
          ? `${errText(err)} — ${done.length} of ${moduleIds.length} module(s) were saved before the error.`
          : errText(err)
      );
      if (done.length) {
        // treat the modules that did save as clean
        setOrigRows((prev) =>
          prev.map((o) => (done.includes(o.moduleId) ? { ...rows.find((r) => rowKey(r) === rowKey(o)) } : o))
        );
      }
    }
    // Refresh the grid in the background (it shows its own "fetching" loader).
    if (!failed) loadPermissions();
  };

  // ---- render -------------------------------------------------------------
  const all = summarize(visibleRows, ALL_KEYS);

  return (
    <>
      {confirmNode}
      <style>{CSS}</style>

      {saving && (
        <div className="perm-overlay" role="status" aria-live="polite" aria-label="Saving permissions">
          <div className="perm-spinner" />
        </div>
      )}

      {snack && (
        <div className={`perm-snack perm-snack--${snack.type}`} role="alert">
          <span>{snack.msg}</span>
          <button type="button" aria-label="Dismiss" onClick={() => setSnack(null)}>×</button>
        </div>
      )}

      <h1 className="page-title">permissions</h1>
      <p className="page-sub">
        {routeClientCode && (
          <>
            <Link to={`/admin/tenants/${routeClientCode}`} style={{ color: 'var(--text-dim)' }}>← {routeClientCode}</Link>
            {'  /  '}
          </>
        )}
        {clientCode && <>client: <b style={{ color: 'var(--green)' }}>{clientCode}</b></>}
      </p>

      {!routeClientCode && (
        <Panel title="// LOOKUP_TENANT">
          <div className="toolbar">
            <div className="toolbar__group">
              <TenantSelect value={clientCode} onSelect={(cc) => setClientCode(cc)} />
            </div>
          </div>
        </Panel>
      )}

      {clientCode && (
        <Panel title="// FEATURE_PERMISSIONS">
          {lookupLoading && <Loader label="loading organizations and roles" />}

          {!lookupLoading && (
            <>
              <div className="perm-grid">
                <div className="perm-field">
                  <label htmlFor="perm-org">Organization Name *</label>
                  <select
                    id="perm-org"
                    className="perm-select"
                    value={orgId}
                    disabled={busy || !orgs.length}
                    onChange={guard(setOrgId)}
                  >
                    {!orgs.length && <option value="">No organizations</option>}
                    {orgs.map((o) => (
                      <option key={o.schoolId} value={String(o.schoolId)}>{o.schoolName}</option>
                    ))}
                  </select>
                </div>

                <div className="perm-field">
                  <label htmlFor="perm-branch">Branch Name *</label>
                  <select
                    id="perm-branch"
                    className="perm-select"
                    value={branchId}
                    disabled={busy || !branches.length}
                    onChange={guard(setBranchId)}
                  >
                    {!branches.length && <option value="">No branches</option>}
                    {branches.map((b) => (
                      <option key={b.branchId} value={String(b.branchId)}>{b.branchName}</option>
                    ))}
                  </select>
                </div>

                <div className="perm-field">
                  <label htmlFor="perm-role">User Role *</label>
                  <select
                    id="perm-role"
                    className="perm-select"
                    value={groupId}
                    disabled={busy || !groups.length}
                    onChange={guard(setGroupId)}
                  >
                    {!groups.length && <option value="">No roles</option>}
                    {groups.map((g) => (
                      <option key={g.userGroupId} value={String(g.userGroupId)}>{g.userGroupName}</option>
                    ))}
                  </select>
                </div>

                <div className="perm-field">
                  <label htmlFor="perm-module">Module Name</label>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <SearchSelect
                      id="perm-module"
                      width="100%"
                      value={activeFilter}
                      disabled={busy || !moduleOptions.length}
                      placeholder="All Module"
                      options={[
                        { value: 'all', label: 'All Module' },
                        ...moduleOptions.map((m) => ({ value: String(m.id), label: m.name })),
                      ]}
                      onChange={changeModule}
                    />
                  </div>
                </div>
              </div>

              {!selectionReady && (
                <EmptyState>Select an organization, branch and user role to see permissions.</EmptyState>
              )}

              {selectionReady && loading && !saving && <Loader label="fetching permissions" />}

              {selectionReady && !loading && rows.length === 0 && (
                <EmptyState>No permission rows returned.</EmptyState>
              )}

              {selectionReady && !loading && rows.length > 0 && (
                <>
                  {/* module quick-select bar */}
                  <div className="perm-modbar">
                    {visibleModules.map((m) => {
                      const s = summarize(rows.filter((r) => r.moduleId === m.id), ALL_KEYS);
                      return (
                        <label key={m.id} className="perm-chip">
                          <TriCheck
                            checked={s.checked}
                            indeterminate={s.indeterminate}
                            disabled={saving}
                            onChange={(v) => toggleModule(m.id, v)}
                            title={`Select every form in ${m.name}`}
                          />
                          {m.name}
                        </label>
                      );
                    })}
                  </div>

                  <div className="perm-actions">
                    <button
                      className="term-btn term-btn--sm term-btn--solid"
                      disabled={saving || dirtyCount === 0}
                      onClick={handleSave}
                    >
                      {saving ? 'saving…' : 'save'}
                    </button>
                    <button
                      className="term-btn term-btn--sm term-btn--ghost"
                      disabled={saving || dirtyCount === 0}
                      onClick={handleCancel}
                    >
                      cancel
                    </button>
                    {dirtyCount > 0 && (
                      <span className="perm-dirty">{dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}</span>
                    )}
                  </div>

                  <div className="perm-tablewrap">
                    <table className="term-table">
                      <thead>
                        <tr>
                          <th style={{ width: 36 }}>
                            <TriCheck
                              checked={all.checked}
                              indeterminate={all.indeterminate}
                              disabled={saving}
                              onChange={toggleAll}
                              title="Select everything shown"
                            />
                          </th>
                          <th>Module Name</th>
                          <th>Form Name</th>
                          {FLAGS.map((f) => {
                            const s = summarize(visibleRows, [f.key]);
                            return (
                              <th key={f.key}>
                                <label className="perm-th">
                                  <TriCheck
                                    checked={s.checked}
                                    indeterminate={s.indeterminate}
                                    disabled={saving}
                                    onChange={(v) => toggleColumn(f.key, v)}
                                  />
                                  {f.label}
                                </label>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {visibleRows.map((row) => {
                          const s = summarize([row], ALL_KEYS);
                          return (
                            <tr key={rowKey(row)} className={dirtyKeys.has(rowKey(row)) ? 'perm-row--dirty' : ''}>
                              <td>
                                <TriCheck
                                  checked={s.checked}
                                  indeterminate={s.indeterminate}
                                  disabled={saving}
                                  onChange={(v) => toggleRow(row, v)}
                                  title="Select all flags for this form"
                                />
                              </td>
                              <td className="perm-mod">{row.moduleName}</td>
                              <td>{row.formName}</td>
                              {FLAGS.map((f) => (
                                <td key={f.key}>
                                  <input
                                    type="checkbox"
                                    checked={!!row[f.key]}
                                    disabled={saving}
                                    onChange={(e) => toggleCell(row, f.key, e.target.checked)}
                                  />
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </Panel>
      )}
    </>
  );
}